/**
 * "New LPO" / "Edit LPO" side panel (22 #5). The total is worked out from the lines (qty × unit price) —
 * a different total can still be entered (lump-sum discounts) and the mismatch is said in words; lines can
 * be removed; part numbers are searched in the catalog (or typed when they are not in it). Opened from
 * Procurement (new / edit) and from Forecast "Create LPO" with the short parts prefilled (23 #4).
 * From Forecast, a part whose shortage is (partly) pieces already delivered that are not in stock says so on
 * its line, and when that explains all of it the quantity is left empty ("Use 20" fills it on purpose) with a
 * "check stock first" note on top — ordering again may buy the same pieces twice. The LPO is saved with
 * source "forecast" (so the shortage maths can count it as supply only).
 * Edit keeps each line's id; a line cannot go below what was already delivered (the API checks too).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ShoppingCart, TriangleAlert, X } from 'lucide-react';
import api from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { formatMoney, formatNumber, toDateInputValue, toNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SheetClose } from '@/components/ui/sheet';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { notify } from '@/components/ui/notify';
import { SearchSelect } from './SearchSelect';
import { LinkButton, MissingText, plural } from './SfUi';
import { lpoLinesTotal, roundMoney } from './procurementModel';
import { isDraftLpo } from './draftsModel';

const fieldLabel = 'text-[12px] font-semibold text-ink-2';
let lineSeq = 0;
const newKey = () => { lineSeq += 1; return `line-${lineSeq}`; };

/** Phones: the panel rises from the bottom instead of covering the whole screen. */
export const PHONE_SHEET_CLASS = 'max-sm:top-auto max-sm:h-[92dvh] max-sm:rounded-t-[20px] max-sm:border-l-0 max-sm:border-t';

const blankLine = () => ({ key: newKey(), id: '', part_number: '', description: '', ordered_qty: '', unit_price: '', delivered: 0 });

/** Catalog search for the part picker: server-side, one option per part number. */
export async function searchCatalogParts(query) {
  const res = await api.get('/catalog', { params: { search: query || undefined, limit: 20 } });
  const data = res.data;
  const items = Array.isArray(data) ? data : (data?.items || []);
  const seen = new Set();
  return items.filter((item) => {
    const key = String(item?.part_number || '').trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  }).map((item) => ({
    value: item.part_number,
    label: item.part_number,
    sub: [item.description, item.preferred_supplier].filter(Boolean).join(' · '),
    description: item.description || '',
  }));
}

function initialState({ mode, lpo, prefill }) {
  if (mode === 'edit' && lpo) {
    const lines = (lpo.line_items || []).map((line) => ({
      key: newKey(),
      id: line.id || '',
      part_number: line.part_number || '',
      description: line.description || '',
      ordered_qty: String(line.ordered_qty ?? ''),
      unit_price: String(line.unit_price ?? ''),
      delivered: Number(line.delivered_qty) || 0,
      required_on_site_date: line.required_on_site_date || '',
    }));
    const linesTotal = roundMoney(lpoLinesTotal(lines));
    const stored = roundMoney(lpo.total_value);
    return {
      form: {
        project_id: lpo.project_name ? (lpo.project_id || '') : '',
        lpo_number: lpo.lpo_number || '',
        supplier: lpo.supplier || '',
        date_received: toDateInputValue(lpo.date_received) || '',
        expected_start_delivery_date: toDateInputValue(lpo.expected_start_delivery_date) || '',
      },
      lines: lines.length ? lines : [blankLine()],
      manualTotal: Math.abs(stored - linesTotal) > 0.005,
      totalValue: String(stored),
    };
  }
  const lines = (prefill?.lines || []).map((line) => ({
    ...blankLine(),
    part_number: line.part_number || '',
    description: line.description || '',
    ordered_qty: line.ordered_qty ? String(line.ordered_qty) : '',
    unit_price: line.unit_price ? String(line.unit_price) : '',
    ...(line.forecast ? { forecast: line.forecast } : {}),
  }));
  return {
    form: {
      project_id: prefill?.project_id || '',
      lpo_number: '',
      supplier: prefill?.supplier || '',
      date_received: toDateInputValue(new Date()) || '',
      expected_start_delivery_date: '',
    },
    lines: lines.length ? lines : [blankLine()],
    manualTotal: false,
    totalValue: '',
  };
}

/** The note under a line prefilled from Forecast whose shortage is (partly) delivered pieces not in stock. */
function ForecastLineNote({ line, onUse }) {
  const f = line.forecast;
  if (!f) return null;
  const where = f.lpoNumber ? <> on <span className="font-mono">{f.lpoNumber}</span></> : null;
  const stock = f.stockOnHand > 0 ? `only ${formatNumber(f.stockOnHand)} ${f.stockOnHand === 1 ? 'is' : 'are'}` : 'none are';
  const site = f.deliveredToSite > 0 ? ` (${formatNumber(f.deliveredToSite)} went direct to site)` : '';
  const qty = toNumber(line.ordered_qty);
  return (
    <p className="mt-1.5 flex items-start gap-1.5 text-[12px] text-status-amber-ink" data-testid={`lpo-line-forecast-${line.part_number}`}>
      <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0">
        {f.buyQty > 0 ? (
          <>Prefilled {formatNumber(f.buyQty)} of the {formatNumber(f.short)} short — the other {formatNumber(f.gapQty)} were delivered{where} but are not in stock{site}.</>
        ) : (
          <>
            Forecast says {formatNumber(f.short)} short, but {formatNumber(f.delivered)} were delivered{where} and {stock} in stock{site}.
            {qty > 0 ? ' Check stock before ordering.' : ' Check stock before ordering — quantity left empty.'}
            {!(qty > 0) ? <> <LinkButton onClick={() => onUse(f.short)} className="font-semibold" data-testid={`lpo-line-use-short-${line.part_number}`}>Use {formatNumber(f.short)}</LinkButton></> : null}
          </>
        )}
      </span>
    </p>
  );
}

const snapshot = (state) => JSON.stringify({
  form: state.form,
  lines: state.lines.map(({ key, ...rest }) => rest),
  manualTotal: state.manualTotal,
  totalValue: state.manualTotal ? state.totalValue : '',
});

/** Lines that are filled in enough to send; a fully blank row is ignored. Throws a readable error. */
/** A line the user started: a part number, a quantity or a price (an untouched blank line is ignored). */
const isFilledLine = (line) => Boolean(String(line.part_number ?? '').trim() || String(line.ordered_qty ?? '').trim() || String(line.unit_price ?? '').trim());

function linesForSave(lines) {
  const filled = lines.filter(isFilledLine);
  return filled.map((line, index) => {
    const part = String(line.part_number || '').trim();
    if (!part) throw new Error(`Line ${index + 1}: pick a part number`);
    const qty = toNumber(line.ordered_qty);
    if (qty === null || !(qty > 0)) throw new Error(`${part}: enter a quantity of more than 0`);
    if (line.delivered > 0 && qty < line.delivered) throw new Error(`${part}: ${formatNumber(line.delivered)} were already delivered — the quantity cannot be less`);
    const priceText = String(line.unit_price ?? '').trim();
    const price = priceText ? toNumber(priceText) : 0;
    if (price === null || price < 0) throw new Error(`${part}: the unit price must be 0 or more`);
    const out = { part_number: part, description: String(line.description || '').trim(), ordered_qty: qty, unit_price: price };
    if (line.id) out.id = line.id;
    if (line.required_on_site_date) out.required_on_site_date = line.required_on_site_date;
    return out;
  });
}

/**
 * props: open, onOpenChange, mode ('create' | 'edit'), lpo (edit: the LPO with line delivered qty),
 * prefill ({ lines, supplier, project_id, source }), projects (optional — loaded when missing),
 * suppliers (names for the supplier suggestions), onSaved(lpo), focus ('project' opens on the project).
 */
export function LpoFormPanel({ open, onOpenChange, mode = 'create', lpo, prefill, projects: projectsProp, suppliers = [], onSaved, focus }) {
  const [state, setState] = useState(() => initialState({ mode, lpo, prefill }));
  const [initial, setInitial] = useState(() => snapshot(state));
  const [loadedProjects, setLoadedProjects] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState({});
  const projectRef = useRef(null);
  const numberRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const next = initialState({ mode, lpo, prefill });
    setState(next);
    setInitial(snapshot(next));
    setError('');
    setInvalid({});
  }, [open, mode, lpo, prefill]);

  useEffect(() => {
    if (!open || projectsProp?.length || loadedProjects) return;
    api.get('/projects').then((res) => setLoadedProjects(Array.isArray(res.data) ? res.data : [])).catch(() => setLoadedProjects([]));
  }, [open, projectsProp, loadedProjects]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => {
      if (focus === 'project') projectRef.current?.focus();
      else if (mode === 'create') numberRef.current?.focus();
    }, 60);
    return () => clearTimeout(timer);
  }, [open, focus, mode]);

  const projects = useMemo(() => (projectsProp?.length ? projectsProp : (loadedProjects || [])), [projectsProp, loadedProjects]);
  const projectOptions = useMemo(
    () => projects.map((project) => ({ value: project.id, label: project.name || 'Unnamed project', sub: [project.client, project.system_type].filter(Boolean).join(' · ') }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [projects]
  );
  const supplierListId = 'lpo-supplier-options';
  // A draft (22 #9) is not an order yet: Send sets its sent and expected dates, so the form has none.
  const isDraft = mode === 'edit' && isDraftLpo(lpo);
  const { form, lines, manualTotal, totalValue } = state;
  const dirty = snapshot(state) !== initial;
  const linesTotal = roundMoney(lpoLinesTotal(lines));
  const pieces = lines.reduce((sum, line) => sum + (toNumber(line.ordered_qty) > 0 ? toNumber(line.ordered_qty) : 0), 0);
  const filledLines = lines.filter(isFilledLine).length;
  const manualValue = toNumber(totalValue);
  const mismatch = manualTotal && manualValue !== null && Math.abs(roundMoney(manualValue) - linesTotal) > 0.005;

  const setForm = (patch) => setState((prev) => ({ ...prev, form: { ...prev.form, ...patch } }));
  const setLine = (key, patch) => setState((prev) => ({ ...prev, lines: prev.lines.map((line) => (line.key === key ? { ...line, ...patch } : line)) }));
  const addLine = () => setState((prev) => ({ ...prev, lines: [...prev.lines, blankLine()] }));
  const removeLine = (key) => setState((prev) => {
    const next = prev.lines.filter((line) => line.key !== key);
    return { ...prev, lines: next.length ? next : [blankLine()] };
  });
  const setManual = (on) => setState((prev) => ({ ...prev, manualTotal: on, totalValue: on ? (prev.totalValue || String(roundMoney(lpoLinesTotal(prev.lines)))) : prev.totalValue }));

  const save = useCallback(async () => {
    setError('');
    const problems = {};
    if (!form.project_id) problems.project = true;
    if (!form.lpo_number.trim()) problems.number = true;
    setInvalid(problems);
    try {
      if (problems.project && problems.number) throw new Error('Pick the project and enter the LPO number');
      if (problems.project) throw new Error('Pick the project this LPO is for');
      if (problems.number) throw new Error('Enter the LPO number');
      const lineItems = linesForSave(lines);
      let total = roundMoney(lpoLinesTotal(lines));
      if (manualTotal) {
        const value = toNumber(totalValue);
        if (value === null || value < 0) throw new Error('The total value must be a number of 0 or more');
        total = roundMoney(value);
      }
      const payload = {
        project_id: form.project_id,
        lpo_number: form.lpo_number.trim(),
        supplier: form.supplier.trim(),
        ...(isDraft ? {} : {
          date_received: form.date_received || '',
          expected_start_delivery_date: form.expected_start_delivery_date || '',
        }),
        total_value: total,
        currency: (mode === 'edit' && lpo?.currency) || 'AED',
        line_items: lineItems,
      };
      if (mode !== 'edit' && prefill?.origin === 'forecast') payload.source = 'forecast';
      setSaving(true);
      const res = mode === 'edit' && lpo?.id
        ? await api.put(`/lpos/${lpo.id}`, payload)
        : await api.post('/lpos', payload);
      notify.success(mode === 'edit' ? `${payload.lpo_number} saved` : `LPO ${payload.lpo_number} created`, {
        description: `${plural(lineItems.length, 'line')} · ${formatMoney(total)}`,
      });
      onSaved?.(res.data);
    } catch (err) {
      const message = getErrorMessage(err, mode === 'edit' ? 'Could not save the LPO' : 'Could not create the LPO');
      setError(message);
      throw new Error(message);
    } finally {
      setSaving(false);
    }
  }, [form, lines, manualTotal, totalValue, mode, lpo, prefill, onSaved, isDraft]);

  const submit = async () => {
    try {
      await save();
      onOpenChange(false);
    } catch {
      // the error is shown in the footer
    }
  };

  const title = isDraft ? `Edit draft ${lpo?.lpo_number || ''}`.trim() : mode === 'edit' ? `Edit ${lpo?.lpo_number || 'LPO'}` : 'New LPO';
  const subtitle = isDraft
    ? 'Price every line from the supplier’s quote. The sent and expected dates are set when you send it.'
    : mode === 'edit'
      ? 'Change the order, its lines or link it to a project. Delivered quantities stay as recorded.'
      : prefill?.source || 'Purchase order or letter of intent from a supplier.';

  const footer = (
    <footer className="flex flex-wrap items-center gap-2 border-t border-line-2 bg-surface px-4 py-3 sm:px-[22px] sm:py-3.5">
      <div className="min-w-0 flex-1 text-[12.5px]">
        {error ? (
          <span className="flex min-w-0 items-start gap-1.5 font-semibold text-status-red-ink" role="alert" data-testid="lpo-form-error">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="min-w-0">{error}</span>
          </span>
        ) : (
          <span className="text-ink-3">
            {plural(filledLines, 'line')} · {formatNumber(pieces)} pcs · <b className="font-semibold text-ink">{formatMoney(manualTotal && manualValue !== null ? manualValue : linesTotal)}</b>
          </span>
        )}
      </div>
      <div className="ml-auto flex items-center gap-2">
        <SheetClose asChild>
          <Button type="button" variant="ghost" disabled={saving}>Cancel</Button>
        </SheetClose>
        <Button type="button" onClick={submit} disabled={saving} data-testid="submit-lpo-btn">
          {saving ? 'Saving…' : mode === 'edit' ? 'Save LPO' : 'Create LPO'}
        </Button>
      </div>
    </footer>
  );

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      subtitle={subtitle}
      icon={ShoppingCart}
      width="lg"
      dirty={dirty}
      onSave={save}
      saving={saving}
      error={error || ''}
      footer={footer}
      className={PHONE_SHEET_CLASS}
      testId="lpo-form"
    >
      {mode !== 'edit' && prefill?.stockGapParts > 0 ? (
        <div className="rounded-xl border border-[color:var(--status-amber-bg)] bg-status-amber-bg px-3.5 py-3 text-[12.5px] text-status-amber-ink" role="note" data-testid="lpo-form-stock-gap">
          <p className="flex items-start gap-2 font-semibold">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            Check stock before ordering
          </p>
          <p className="ml-6 mt-1">
            {prefill.stockGapParts === 1 ? '1 part on this order is' : `${prefill.stockGapParts} parts on this order are`} short because pieces
            already delivered on earlier LPOs are not in stock — in another store, already issued, or delivered direct to
            site. Find them first: ordering again may buy the same pieces twice.{' '}
            <Link to="/inventory?tab=stock" className="font-semibold text-brand-ink hover:underline">Open Inventory</Link>
          </p>
        </div>
      ) : null}
      <SidePanelSection title="Order">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <Label className={fieldLabel} htmlFor="lpo-project">Project *</Label>
            <SearchSelect
              ref={projectRef}
              id="lpo-project"
              value={form.project_id}
              onChange={(value) => { setForm({ project_id: value }); setInvalid((prev) => ({ ...prev, project: false })); }}
              options={projectOptions}
              placeholder="Pick the project"
              searchPlaceholder="Search projects"
              emptyText={projects.length ? 'No project matches' : 'Loading projects…'}
              invalid={invalid.project}
              testId="lpo-project-select"
            />
            {mode === 'edit' && lpo && !lpo.project_name && !form.project_id ? (
              <MissingText className="block">No project linked — pick the project this order is for.</MissingText>
            ) : null}
          </div>
          <div className="space-y-1">
            <Label className={fieldLabel} htmlFor="lpo-number">LPO number *</Label>
            <Input
              ref={numberRef}
              id="lpo-number"
              value={form.lpo_number}
              onChange={(e) => { setForm({ lpo_number: e.target.value }); setInvalid((prev) => ({ ...prev, number: false })); }}
              aria-invalid={invalid.number || undefined}
              className={cn('font-mono placeholder:font-sans placeholder:text-ink-4', invalid.number && 'border-[color:var(--status-red)]')}
              placeholder="e.g. LPO-2026-0xx"
              data-testid="lpo-number-input"
            />
          </div>
          <div className="space-y-1">
            <Label className={fieldLabel} htmlFor="lpo-supplier">Supplier</Label>
            <Input id="lpo-supplier" list={supplierListId} value={form.supplier} onChange={(e) => setForm({ supplier: e.target.value })} placeholder="Supplier name" className="placeholder:text-ink-4" />
            <datalist id={supplierListId}>
              {[...new Set(suppliers.filter(Boolean))].sort().map((name) => <option key={name} value={name} />)}
            </datalist>
          </div>
          {isDraft ? (
            <p className="text-[12px] text-ink-3 sm:col-span-2" data-testid="lpo-form-draft-dates">
              A draft has no sent or expected date yet — you enter them in <b className="font-semibold text-ink-2">Send to supplier</b>.
            </p>
          ) : (
            <>
              <div className="space-y-1">
                <Label className={fieldLabel} htmlFor="lpo-date">Date received</Label>
                <Input id="lpo-date" type="date" value={form.date_received} onChange={(e) => setForm({ date_received: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className={fieldLabel} htmlFor="lpo-expected">Expected delivery</Label>
                <Input id="lpo-expected" type="date" value={form.expected_start_delivery_date} onChange={(e) => setForm({ expected_start_delivery_date: e.target.value })} data-testid="lpo-expected-input" />
              </div>
            </>
          )}
        </div>
      </SidePanelSection>

      <SidePanelSection title={`Lines · ${formatNumber(filledLines)}`} actions={<Button type="button" variant="ghost" size="sm" onClick={addLine} data-testid="lpo-add-line"><Plus aria-hidden="true" /> Add line</Button>}>
        <div className="hidden grid-cols-[minmax(0,1fr)_76px_104px_96px_28px] gap-2 px-0.5 text-[11px] font-bold uppercase tracking-[0.04em] text-ink-3 sm:grid">
          <span>Part</span><span className="text-right">Qty</span><span className="text-right">Unit price</span><span className="text-right">Line total</span><span />
        </div>
        <ul className="space-y-2" data-testid="lpo-form-lines">
          {lines.map((line, index) => {
            const qty = toNumber(line.ordered_qty);
            const price = toNumber(line.unit_price);
            const total = qty > 0 && price >= 0 && qty !== null && price !== null ? qty * price : null;
            const locked = line.delivered > 0;
            return (
              <li key={line.key} className="rounded-xl border border-line-2 bg-surface-2 p-2" data-testid={`lpo-line-${index}`}>
                <div className="grid grid-cols-[minmax(0,1fr)_76px_28px] items-center gap-2 sm:grid-cols-[minmax(0,1fr)_76px_104px_96px_28px]">
                  <SearchSelect
                    value={line.part_number}
                    onChange={(value, option) => setLine(line.key, { part_number: value, description: line.description || option?.description || '' })}
                    loadOptions={searchCatalogParts}
                    allowCustom
                    mono
                    placeholder="Part number"
                    searchPlaceholder="Search part no. or description"
                    emptyText="No catalog part matches — type it to use it"
                    aria-label={`Part number, line ${index + 1}`}
                    disabled={locked}
                    testId={`lpo-line-part-${index}`}
                  />
                  <Input
                    type="number"
                    inputMode="decimal"
                    min={locked ? line.delivered : 0}
                    step="any"
                    value={line.ordered_qty}
                    onChange={(e) => setLine(line.key, { ordered_qty: e.target.value })}
                    aria-label={`Quantity, line ${index + 1}`}
                    className="h-9 text-right tabular-nums"
                    data-testid={`lpo-line-qty-${index}`}
                  />
                  <Input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="any"
                    value={line.unit_price}
                    onChange={(e) => setLine(line.key, { unit_price: e.target.value })}
                    aria-label={`Unit price (AED), line ${index + 1}`}
                    placeholder="0.00"
                    className="col-span-2 h-9 text-right tabular-nums sm:col-span-1"
                    data-testid={`lpo-line-price-${index}`}
                  />
                  <span className="hidden text-right text-[13px] font-semibold tabular-nums text-ink sm:block">
                    {total !== null ? formatNumber(total, { decimals: 2 }) : <span className="text-ink-4">—</span>}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => removeLine(line.key)}
                    disabled={locked}
                    title={locked ? `${formatNumber(line.delivered)} already delivered — this line stays` : 'Remove line'}
                    aria-label={`Remove line ${index + 1}${line.part_number ? ` (${line.part_number})` : ''}`}
                    className="row-start-1 text-ink-3 sm:row-auto"
                    style={{ gridColumn: '-2 / -1' }}
                    data-testid={`lpo-line-remove-${index}`}
                  >
                    <X aria-hidden="true" />
                  </Button>
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                  <Input
                    value={line.description}
                    onChange={(e) => setLine(line.key, { description: e.target.value })}
                    placeholder="Description"
                    aria-label={`Description, line ${index + 1}`}
                    className="h-8 flex-1 bg-surface text-[12.5px]"
                  />
                  {locked ? <span className="shrink-0 text-[11.5px] font-semibold text-ink-3">{formatNumber(line.delivered)} delivered</span> : null}
                </div>
                <ForecastLineNote line={line} onUse={(qty) => setLine(line.key, { ordered_qty: String(qty) })} />
              </li>
            );
          })}
        </ul>
      </SidePanelSection>

      <SidePanelSection title="Value">
        <div className="rounded-xl border border-line bg-surface px-3.5 py-3" data-testid="lpo-form-total">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <span className="text-[12.5px] text-ink-3">Lines add up to</span>
            <span className="font-heading text-[18px] font-extrabold tabular-nums text-ink">{formatMoney(linesTotal)}</span>
          </div>
          {manualTotal ? (
            <div className="mt-2.5 space-y-1.5 border-t border-line-2 pt-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className={fieldLabel} htmlFor="lpo-total">LPO value (AED)</Label>
                <LinkButton onClick={() => setManual(false)}>Use the lines total</LinkButton>
              </div>
              <Input id="lpo-total" type="number" inputMode="decimal" min="0" step="any" value={totalValue} onChange={(e) => setState((prev) => ({ ...prev, totalValue: e.target.value }))} className="text-right tabular-nums" data-testid="lpo-total-input" />
              {mismatch ? (
                <p className="flex items-start gap-1.5 text-[12px] font-semibold text-status-amber-ink" data-testid="lpo-total-mismatch">
                  <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  Lines add up to {formatNumber(linesTotal, { decimals: 2 })} · the LPO value says {formatNumber(manualValue, { decimals: 2 })}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="mt-1 text-[12px] text-ink-3">
              The LPO value is the lines total.{' '}
              <LinkButton onClick={() => setManual(true)} data-testid="lpo-total-manual">Enter a different value</LinkButton>
            </p>
          )}
        </div>
      </SidePanelSection>
    </SidePanel>
  );
}
